import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'

export interface FeatureFlagEntry {
  label: string
  enabled: boolean
}

export interface FeatureFlagsResponse {
  enabled_features: string[]
  all_features: Record<string, FeatureFlagEntry>
}

/**
 * Fetches the feature flags for the current user's company.
 * Cached for 5 minutes — features rarely change without a page reload.
 */
export function useFeatureFlags() {
  return useQuery<FeatureFlagsResponse>({
    queryKey: ['feature-flags'],
    queryFn: () =>
      apiClient.get('/api/me/features/').then((r) => r.data.data as FeatureFlagsResponse),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000,
    // Flags only change on a plan/config change (page reload). Keep them STABLE
    // across client-side navigation: the global default `refetchOnMount:'always'`
    // would otherwise refetch on every gated-route mount, and a transient/empty
    // refetch made FeatureGate read "disabled" and bounce the user to /404 — the
    // WhatsApp Flows page showed blank until a hard refresh. Opt out of both
    // refetch triggers so the first successful load stays authoritative.
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 2,
  })
}

/**
 * Returns true if the given feature key is enabled for the current company.
 * Falls back to false while loading or if the company has no flag set.
 */
export function useIsFeatureEnabled(featureKey: string): boolean {
  const { data } = useFeatureFlags()
  return data?.enabled_features?.includes(featureKey) ?? false
}

// ---------------------------------------------------------------------------
// Feature key constants (mirrors backend utils/feature_flags.py)
// ---------------------------------------------------------------------------

export const FEATURES = {
  WHATSAPP_FLOWS: 'whatsapp_flows',
  PAYMENTS: 'payments',
  ECOMMERCE: 'ecommerce',
  AI_CHATBOT: 'ai_chatbot',
} as const

export type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES]
